import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/components/dialog';
import { Label } from '@/shared/ui/components/label';
import { apiErrorMessage } from '@/shared/api-client';
import {
  useCountryReferenceData,
  useCreateCountryPolicy,
  type ReferenceItem,
} from './countryPolicies';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

function Picker({
  id,
  label,
  value,
  onChange,
  options,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReferenceItem[];
}) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{label}</Label>
      <select
        id={id}
        required
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs"
      >
        <option value="" disabled>
          —
        </option>
        {options.map((option) => (
          <option key={option.code} value={option.code}>
            {option.label} ({option.code})
          </option>
        ))}
      </select>
    </div>
  );
}

export function CreateCountryPolicyDialog({ open, onOpenChange, onCreated }: Props) {
  const { t } = useTranslation('common');
  const reference = useCountryReferenceData(open);
  const createPolicy = useCreateCountryPolicy();

  const [countryCode, setCountryCode] = useState('');
  const [language, setLanguage] = useState('');
  const [currency, setCurrency] = useState('');
  const [timezone, setTimezone] = useState('');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setCountryCode('');
    setLanguage('');
    setCurrency('');
    setTimezone('');
    setError(null);
  }

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  function handleOpenChange(next: boolean) {
    onOpenChange(next);
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await createPolicy.mutateAsync({
        countryCode,
        defaultLanguage: language,
        defaultCurrency: currency,
        defaultTimezone: timezone,
      });
      handleOpenChange(false);
      onCreated();
    } catch (caught) {
      setError(apiErrorMessage(t, 'countryPolicies', caught));
    }
  }

  const timezones: ReferenceItem[] =
    reference.data?.timezones.map((id) => ({ code: id, label: id })) ?? [];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('countryPolicies.create.title')}</DialogTitle>
            <DialogDescription>{t('countryPolicies.create.description')}</DialogDescription>
          </DialogHeader>

          {reference.isLoading && (
            <p className="text-sm text-muted-foreground">
              {t('countryPolicies.reference.loading')}
            </p>
          )}
          {reference.isError && (
            <p className="text-sm text-destructive">{t('countryPolicies.reference.loadFailed')}</p>
          )}

          {reference.data && (
            <>
              <Picker
                id="cp-country"
                label={t('countryPolicies.fields.country')}
                value={countryCode}
                onChange={setCountryCode}
                options={reference.data.countries}
              />
              <Picker
                id="cp-language"
                label={t('countryPolicies.fields.language')}
                value={language}
                onChange={setLanguage}
                options={reference.data.languages}
              />
              <Picker
                id="cp-currency"
                label={t('countryPolicies.fields.currency')}
                value={currency}
                onChange={setCurrency}
                options={reference.data.currencies}
              />
              <Picker
                id="cp-timezone"
                label={t('countryPolicies.fields.timezone')}
                value={timezone}
                onChange={setTimezone}
                options={timezones}
              />
            </>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={createPolicy.isPending || !reference.data}>
              {createPolicy.isPending
                ? t('countryPolicies.create.submitting')
                : t('countryPolicies.create.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
